import type {
  ConditionFunctionConfig,
  ConditionsConfig,
  ConditionsConfig_ConditionConfig,
  Job,
  OutputData,
  OutputReference,
  ValueConfig,
  ValueData,
} from "@smartsuite/automation-engine-api";
import { valueDataToStrings } from "@smartsuite/automation-conditions";
import { resolveOutputReference } from "../../../shared/status-walk";

/**
 * A condition reference resolved to display data: the value the Matched
 * Conditions box shows plus the output's own stored label (the fallback row
 * label when the field no longer resolves in the current schema).
 */
// eslint-disable-next-line import/no-unused-modules -- the payload of ReferenceValueResolver below; formatMatchedConditionValues consumes it structurally
export interface ResolvedReferenceValue {
  value: string;
  outputLabel: string;
}

// eslint-disable-next-line import/no-unused-modules -- the resolver contract both value strategies below implement; formatMatchedConditionValues consumes it structurally
export type ReferenceValueResolver = (
  reference: OutputReference,
) => ResolvedReferenceValue | undefined;

// valueDataToStrings, not valueDataToString: the latter is
// valueDataToStringsWithDelimiters(...).join(""), and that delimiter is only
// emitted between `fromReference` values -- so a field matching several plain
// options (Multiple Select / Status) came out concatenated as "Test1Test2Test3"
// (BUG-21276). Joining the parts ourselves separates every matched value.
//
// The converters throw on malformed ValueData (fieldMatching / undefined
// oneofKind); these run in a render path, so a bad value must degrade to
// "unresolvable", not crash.
const displayValue = (
  value: ValueData | undefined,
  timezone: string | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  try {
    const parts = valueDataToStrings(value, timezone)
      .map((part) => part.trim())
      .filter((part) => part !== "");
    return parts.length === 0 ? undefined : parts.join(", ");
  } catch {
    return undefined;
  }
};

const resolvedValue = (
  output: OutputData | undefined,
  timezone: string | undefined,
): ResolvedReferenceValue | undefined => {
  const value = displayValue(output?.value, timezone);
  return value === undefined || output === undefined
    ? undefined
    : { value, outputLabel: output.outputLabel };
};

/**
 * Api-7.25 path: GetBranchData already returned the exact values the branch's
 * conditions were evaluated against; a reference resolves to the output with
 * its outputId. (The response also carries generic record outputs -- ID,
 * Record ID, etc. -- which simply never match a condition reference and so
 * never render.) The payload is a flat list without source info -- callers
 * must route references whose outputId is ambiguous across sources (see
 * ambiguousOutputIds) to the source-scoped job walk instead.
 */
export const referenceValueFromOutputs =
  (
    outputs: OutputData[] | undefined,
    timezone: string | undefined,
  ): ReferenceValueResolver =>
  (reference) =>
    resolvedValue(
      outputs?.find((output) => output.outputId === reference.outputId),
      timezone,
    );

/**
 * Legacy (pre-7.25, empty branchId) path: resolve the reference against the
 * job's own status tree. `loopRecordIndex` is the loop-iteration index the
 * branch executed in, so action-sourced references resolve against that
 * iteration's outputs instead of always the first iteration's.
 */
export const referenceValueFromJob =
  (job: Job, loopRecordIndex?: number): ReferenceValueResolver =>
  (reference) =>
    resolvedValue(
      resolveOutputReference(reference, job, loopRecordIndex),
      job.automation?.timezone,
    );

/**
 * Every ConditionFunctionConfig kind that carries a right-side ValueConfig
 * (`right`/`haystack`/`min`+`max`), mirroring the exhaustive switch in
 * PhraseBuilder/conditions.ts's conditionsText -- plus `between`/
 * `betweenOrEquals`, which that switch doesn't format today but which still
 * carry resolvable references. `changes` recurses into its `before`/`after`
 * sub-functions. Kinds with no ValueConfig field fall through to `[]`.
 */
const extractFunctionValueConfigs = (
  fn: ConditionFunctionConfig | undefined,
): ValueConfig[] => {
  const definedOnly = (
    configs: Array<ValueConfig | undefined>,
  ): ValueConfig[] =>
    configs.filter((config): config is ValueConfig => config !== undefined);

  switch (fn?.function.oneofKind) {
    case "isEquals":
      return definedOnly([fn.function.isEquals.right]);
    case "isNotEquals":
      return definedOnly([fn.function.isNotEquals.right]);
    case "isAnyOf":
      return definedOnly([fn.function.isAnyOf.right]);
    case "isNoneOf":
      return definedOnly([fn.function.isNoneOf.right]);
    case "hasAnyOf":
      return definedOnly([fn.function.hasAnyOf.haystack]);
    case "hasAllOf":
      return definedOnly([fn.function.hasAllOf.haystack]);
    case "hasNoneOf":
      return definedOnly([fn.function.hasNoneOf.haystack]);
    case "greaterThan":
      return definedOnly([fn.function.greaterThan.right]);
    case "greaterThanOrEquals":
      return definedOnly([fn.function.greaterThanOrEquals.right]);
    case "lessThan":
      return definedOnly([fn.function.lessThan.right]);
    case "lessThanOrEquals":
      return definedOnly([fn.function.lessThanOrEquals.right]);
    case "between":
      return definedOnly([fn.function.between.min, fn.function.between.max]);
    case "betweenOrEquals":
      return definedOnly([
        fn.function.betweenOrEquals.min,
        fn.function.betweenOrEquals.max,
      ]);
    case "contains":
      return definedOnly([fn.function.contains.right]);
    case "notContains":
      return definedOnly([fn.function.notContains.right]);
    case "percentCompleteGreaterThan":
      return definedOnly([fn.function.percentCompleteGreaterThan.right]);
    case "percentCompleteLessThan":
      return definedOnly([fn.function.percentCompleteLessThan.right]);
    case "changes":
      return [
        ...extractFunctionValueConfigs(fn.function.changes.before),
        ...extractFunctionValueConfigs(fn.function.changes.after),
      ];
    default:
      return [];
  }
};

/**
 * References live either directly in a ValueConfig_V (`oneofKind ===
 * "reference"`) or nested inside a keyValue operand's `key`/`value`
 * sub-ValueConfigs -- mirroring the recursive `case "keyValue"` in
 * PhraseBuilder/helper.ts's valueConfigVToDescriptionString.
 */
const extractReferencesFromValueConfig = (
  valueConfig: ValueConfig,
): OutputReference[] =>
  valueConfig.values.flatMap((v): OutputReference[] => {
    if (v.value?.oneofKind === "reference") {
      return [v.value.reference];
    }
    if (v.value?.oneofKind === "keyValue") {
      const { key, value } = v.value.keyValue;
      return [
        ...(key !== undefined ? extractReferencesFromValueConfig(key) : []),
        ...(value !== undefined ? extractReferencesFromValueConfig(value) : []),
      ];
    }
    return [];
  });

/**
 * Only the references on a condition's RIGHT side (compare-to-field values,
 * keyValue operands) -- the left side is addressed separately through
 * `condition.outputReference`. The condition lines need these on their own to
 * label each right-side variable with its own source (BUG-21770).
 */
export const conditionValueReferences = (
  condition: ConditionsConfig_ConditionConfig,
): OutputReference[] =>
  extractFunctionValueConfigs(condition.function).flatMap(
    extractReferencesFromValueConfig,
  );

/** Left `outputReference` plus every reference among the right-side ValueConfigs. */
const extractConditionReferences = (
  condition: ConditionsConfig_ConditionConfig,
): OutputReference[] => [
  ...(condition.outputReference !== undefined
    ? [condition.outputReference]
    : []),
  ...extractFunctionValueConfigs(condition.function).flatMap(
    extractReferencesFromValueConfig,
  ),
];

/**
 * Every output reference a branch's conditions were evaluated against, in
 * condition order: each condition's left side first, then its right-side
 * references (compare-to-field values, keyValue operands). Nested condition
 * sub-groups (oneofKind === "config") are skipped, matching the same
 * limitation in formatBranchConditions.
 */
export const conditionReferences = (
  conditions: ConditionsConfig,
): OutputReference[] =>
  conditions.group.flatMap((group) =>
    group.group.oneofKind === "condition"
      ? extractConditionReferences(group.group.condition)
      : [],
  );

const referenceSource = (reference: OutputReference): string =>
  reference.source.oneofKind === "triggerReference"
    ? "trigger"
    : reference.source.oneofKind === "actionReference"
      ? `action-${reference.source.actionReference.instanceId}`
      : reference.source.oneofKind === "loopItem"
        ? `loop-${reference.source.loopItem.instanceId}`
        : "unknown";

/** Stable identity of what a reference points at (source + outputId). */
export const referenceKey = (reference: OutputReference): string =>
  `${referenceSource(reference)}:${reference.outputId}`;

/**
 * outputIds referenced from more than one distinct source among `references`.
 * GetBranchData's flat outputs can't tell such references apart (no per-entry
 * source), so their values must resolve via the source-scoped job walk.
 */
export const ambiguousOutputIds = (
  references: OutputReference[],
): Set<string> => {
  const sourcesById = new Map<string, Set<string>>();
  for (const reference of references) {
    const sources = sourcesById.get(reference.outputId) ?? new Set<string>();
    sources.add(referenceSource(reference));
    sourcesById.set(reference.outputId, sources);
  }
  return new Set(
    [...sourcesById.entries()]
      .filter(([, sources]) => sources.size > 1)
      .map(([outputId]) => outputId),
  );
};

/**
 * Value strategy for the engine path (api 7.25+, branchId present), gated on
 * the engine's verdicts: since STR-14148 the engine stores in BranchData only
 * the outputs of the conditions to list as matched -- every condition of a
 * passing AND group, only the FIRST passed condition of an OR group -- so an
 * outputId absent from the payload means "not a matched condition" and the
 * row must be dropped, NOT resolved through the job walk, which knows
 * values, not verdicts. For an outputId that IS present, value resolution keeps the
 * pre-existing routing: ambiguous outputIds (same id referenced from two
 * sources -- the flat payload can't tell them apart) and present outputs
 * whose stored value doesn't render resolve via the source-scoped job walk.
 *
 * Jobs recorded before the engine change stored every evaluated output, so
 * all their references pass the presence gate and keep the old (over-listing)
 * behavior -- history can't be re-judged retroactively.
 */
export const referenceValueFromMatchedOutputs = (
  outputs: OutputData[] | undefined,
  timezone: string | undefined,
  references: OutputReference[],
  legacyResolve: ReferenceValueResolver,
): ReferenceValueResolver => {
  const engineResolve = referenceValueFromOutputs(outputs, timezone);
  const ambiguous = ambiguousOutputIds(references);

  // Deduction, the last resort for an ambiguous id. The job walk is
  // source-aware and stays authoritative, but it only reaches the trigger and
  // an action's own status -- an action's FIELD values live on its record job,
  // so `[Trigger].[Title] contains [Find].[Title]` lost the right-hand row
  // entirely even though the payload carried its value (BUG-21770). The flat
  // payload has no per-entry source, so instead of assuming an order: let each
  // value the walk DID resolve claim the entry carrying it, and pair what is
  // left only when exactly one reference faces exactly one entry. Anything
  // still ambiguous keeps being dropped -- a missing row beats a value
  // confidently attributed to the wrong source.
  const deduced = new Map<string, ResolvedReferenceValue>();
  for (const outputId of ambiguousOutputIds(references)) {
    const unclaimed = (outputs ?? []).filter(
      (output) => output.outputId === outputId,
    );
    // Absent from the payload means "not a matched condition" (STR-14148):
    // the rows are dropped and the job walk is never consulted, so deduction
    // must not consult it either.
    if (unclaimed.length === 0) continue;
    const unresolved: OutputReference[] = [];
    const seen = new Set<string>();
    for (const reference of references) {
      if (reference.outputId !== outputId) continue;
      const key = referenceKey(reference);
      if (seen.has(key)) continue;
      seen.add(key);

      const walked = legacyResolve(reference);
      if (walked === undefined) {
        unresolved.push(reference);
        continue;
      }
      const claimed = unclaimed.findIndex(
        (output) => displayValue(output.value, timezone) === walked.value,
      );
      if (claimed !== -1) unclaimed.splice(claimed, 1);
    }
    if (unresolved.length === 1 && unclaimed.length === 1) {
      const forced = resolvedValue(unclaimed[0], timezone);
      if (forced !== undefined) {
        deduced.set(referenceKey(unresolved[0]), forced);
      }
    }
  }

  return (reference) => {
    if (!outputs?.some((output) => output.outputId === reference.outputId)) {
      return undefined;
    }
    return ambiguous.has(reference.outputId)
      ? (legacyResolve(reference) ?? deduced.get(referenceKey(reference)))
      : (engineResolve(reference) ?? legacyResolve(reference));
  };
};
